import { Component, DestroyRef, HostBinding, HostListener, OnInit, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Router, NavigationEnd } from '@angular/router';
import { filter, pairwise, take } from 'rxjs/operators';
import { Language, NavigationItem, UserProfile, AppBarProfileAction, EkoLanguageService, EkoThemeService, EkoBrowserTitleService, hideIndexLoadingScreen, setupKeycloakFrontChannelLogoutListener } from '@ekowitryna/ui';

import { MenuItem } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { KeycloakProfile } from 'keycloak-js';
import Keycloak from 'keycloak-js';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  typeEventArgs,
  ReadyArgs,
} from 'keycloak-angular';
import { TranslateService } from '@ngx-translate/core';

import packageJson from '../../package.json';
import { CompanyContextService } from './core/services/company-context.service';
import { AccountSetupService } from './core/services/account-setup.service';
import { CompanyDataSetupService } from './core/services/company-data-setup.service';
import { PackageDisplayService } from './core/services/package-display.service';
import { EKOWITRYNA_SUBSCRIPTION_URL } from './core/constants/subscription-url';
import { getUserRoles } from './core/auth/keycloak-roles.util';
import {
  AppAccessDeniedReason,
  AppAccessService,
} from './core/services/subscription/app-access.service';
import { AppAccessRuntimeService } from './core/services/subscription/app-access-runtime.service';

const MOBILE_BREAKPOINT_PX = 1024; // zgodne z Tailwind lg
const SUPPORTED_LANGUAGE_CODES = ['pl', 'en', 'de', 'es', 'uk', 'fr'] as const;
const KOBIZE_APP_CODE = 'kobize';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnInit {

  private readonly accountSetupService = inject(AccountSetupService);
  private readonly companyDataSetupService = inject(CompanyDataSetupService);
  private readonly packageDisplay = inject(PackageDisplayService);
  private readonly darkModeService = inject(EkoThemeService);
  private readonly languageService = inject(EkoLanguageService);
  private readonly browserTitle = inject(EkoBrowserTitleService);
  private readonly appAccess = inject(AppAccessService);
  private readonly appAccessRuntime = inject(AppAccessRuntimeService);

  isAdminMode: boolean = false;
  isInitializing = true;
  appVersion: string = packageJson.version;
  hasAccess: boolean | null = null;
  accessDeniedReason?: AppAccessDeniedReason;

  sidebarCollapsed = false;
  isMobile = false;
  keycloakUrl: string = environment.keycloakUrl;
  keycloakRealm: string = environment.realm;
  keycloakClientId: string = environment.clientId;
  pageTitle = 'Ekowitryna';
  availableLanguages: Language[] = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'uk', name: 'Українська', flag: 'ua' },
    { code: 'fr', name: 'Français' }
  ];
  currentRoute = '';
  get currentLanguage(): string {
    return this.languageService.currentLanguage;
  }

  navigationItems: NavigationItem[] = [];
  userMenuItems: MenuItem[] = [];
  currentUser: UserProfile | null = null;
  authenticated = false;
  profileActions: AppBarProfileAction[] = [];
  profileExternalUrl = `${environment.platformWebUrl.replace(/\/$/, '')}/profile`;
  readonly subscriptionUrl = EKOWITRYNA_SUBSCRIPTION_URL;
  requireFirstConfiguration = false;
  requireCompanyData = false;

  get showFirstConfigurationOverlay(): boolean {
    return this.requireFirstConfiguration && this.authenticated && this.hasAccess !== false;
  }

  get showCompanyDataOverlay(): boolean {
    return this.requireCompanyData
      && this.authenticated
      && this.hasAccess !== false
      && !this.requireFirstConfiguration;
  }

  /** App bar labels (translated) */
  loginLabel = '';
  userMenuLabel = '';
  myProfileLabel = '';
  logoutLabel = '';
  buySubscriptionLabel = '';
  themeLabel = '';
  languageLabel = '';
  toggleSidebarLabel = '';

  private keycloakReady = false;
  private profileReady = false;
  private i18nReady = false;

  @HostBinding('class.sidebar-open-mobile') get sidebarOpenMobile(): boolean {
    return this.isMobile && !this.sidebarCollapsed;
  }

  @HostBinding('attr.data-initialized')
  get shellInitialized(): string | null {
    return this.isInitializing ? null : 'true';
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.showFirstConfigurationOverlay || this.showCompanyDataOverlay) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private readonly keycloak = inject(Keycloak);
  private readonly keycloakEvents = inject(KEYCLOAK_EVENT_SIGNAL);
  private frontChannelLogoutCleanup?: () => void;

  constructor(
    private router: Router,
    private primeNG: PrimeNG,
    private translate: TranslateService,
    private readonly companyContext: CompanyContextService,
    private readonly destroyRef: DestroyRef
  ) {
    effect(() => {
      const event = this.keycloakEvents();
      if (event.type === KeycloakEventType.Ready) {
        const authenticated = typeEventArgs<ReadyArgs>(event.args);
        this.keycloakReady = true;
        if (!authenticated) {
          this.profileReady = true;
          this.tryFinishInitialization();
        } else {
          void this.loadUserProfile().finally(() => {
            this.profileReady = true;
            this.tryFinishInitialization();
          });
        }
      }
    });

    effect(() => {
      const event = this.keycloakEvents();
      if (event.type === KeycloakEventType.TokenExpired) {
        void this.keycloak.updateToken(20).catch(() => {
          console.warn('Keycloak token refresh failed (session may have expired)');
        });
      }
    });

    effect(() => {
      const event = this.keycloakEvents();
      if (event.type === KeycloakEventType.AuthSuccess) {
        this.packageDisplay.invalidateCache();
        void this.loadUserProfile();
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects;
      this.currentRoute = url;
      this.isAdminMode = url.startsWith('/admin');
    });

    this.companyContext.selectedCompanyId$
      .pipe(
        pairwise(),
        filter(
          ([previous, current]) =>
            previous != null && current != null && String(previous) !== String(current)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.router.navigate(['/reports'], { replaceUrl: true });
      });

    this.destroyRef.onDestroy(() => this.frontChannelLogoutCleanup?.());
  }

  ngOnInit(): void {
    this.appAccessRuntime.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        if (state.blocked) {
          this.hasAccess = false;
          this.accessDeniedReason = state.reason;
        }
      });

    this.frontChannelLogoutCleanup = setupKeycloakFrontChannelLogoutListener(this.keycloak);

    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }

    this.currentRoute = this.router.url;

    this.accountSetupService.setupRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.loading) {
          return;
        }
        const wasFirstConfigurationRequired = this.requireFirstConfiguration;
        this.requireFirstConfiguration = state.required;
        if (wasFirstConfigurationRequired && !state.required) {
          void this.companyDataSetupService.check();
        }
      });

    this.companyDataSetupService.companyDataRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.loading) {
          return;
        }
        this.requireCompanyData = state.required;
      });

    void this.loadUserProfile().finally(() => {
      this.profileReady = true;
      this.tryFinishInitialization();
    });

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshNavigationAndProfileLabels());
    this.refreshNavigationAndProfileLabels();
  }

  private refreshNavigationAndProfileLabels(): void {
    const keys = [
      'headerProfile.login',
      'headerProfile.consultations',
      'headerProfile.myProfile',
      'headerProfile.userMenu',
      'headerProfile.logout',
      'headerProfile.buySubscription',
      'settings.theme',
      'common.language',
      'a11y.toggleSidebar',
      'menu.calculator'
    ];

    this.translate.get(keys).subscribe(translations => {
      this.loginLabel = translations['headerProfile.login'] ?? '';
      this.userMenuLabel = translations['headerProfile.userMenu'] ?? '';
      this.myProfileLabel = translations['headerProfile.myProfile'] ?? '';
      this.logoutLabel = translations['headerProfile.logout'] ?? '';
      this.buySubscriptionLabel = translations['headerProfile.buySubscription'] ?? '';
      this.themeLabel = translations['settings.theme'] ?? '';
      this.languageLabel = translations['common.language'] ?? '';
      this.toggleSidebarLabel = translations['a11y.toggleSidebar'] ?? '';

      this.profileActions = [
        { label: translations['headerProfile.consultations'] ?? '', action: 'consultations', icon: 'pi pi-calendar' }
      ];

      this.navigationItems = [
        {
          label: translations['menu.calculator'] ?? '',
          icon: 'pi pi-calculator',
          routerLink: '/reports'
        }
      ];
      this.browserTitle.setNavigationItems(this.navigationItems);

      if (!this.i18nReady) {
        this.i18nReady = true;
        this.tryFinishInitialization();
      }
    });
  }

  private tryFinishInitialization(): void {
    if (this.keycloakReady && this.profileReady && this.i18nReady) {
      this.isInitializing = false;
      hideIndexLoadingScreen();
    }
  }

  private profileLoadInFlight: Promise<void> | null = null;

  async loadUserProfile() {
    if (this.profileLoadInFlight) {
      return this.profileLoadInFlight;
    }

    this.profileLoadInFlight = this.loadUserProfileInternal();
    try {
      await this.profileLoadInFlight;
    } finally {
      this.profileLoadInFlight = null;
    }
  }

  private async loadUserProfileInternal() {
    await this.waitForKeycloakReady();
    this.authenticated = this.keycloak.authenticated ?? false;

    if (!this.authenticated) {
      this.darkModeService.initFromStorageOrSystem();
      this.currentUser = null;
      this.requireFirstConfiguration = false;
      this.requireCompanyData = false;
      this.hasAccess = true;
      this.accessDeniedReason = undefined;
      return;
    }

    try {
      const profile: KeycloakProfile = await this.keycloak.loadUserProfile();
      const roles = getUserRoles(this.keycloak);

      this.currentUser = {
        name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || '',
        email: profile.email,
        avatar: `https://ui-avatars.com/api/?size=128&background=random&name=${profile.firstName}+${profile.lastName}`,
        role: roles && roles.length > 0 ? roles[0] : undefined,
        subscriptionBadge: 'Free',
      };
      this.applySubscriptionBadge();

      await this.darkModeService.syncFromProfile();
      await this.languageService.syncFromProfile([...SUPPORTED_LANGUAGE_CODES]);

      const decision = await new Promise<{ hasAccess: boolean; reason?: AppAccessDeniedReason }>((resolve) => {
        this.appAccess.resolveAccess(KOBIZE_APP_CODE).pipe(take(1)).subscribe(resolve);
      });
      this.hasAccess = decision.hasAccess;
      this.accessDeniedReason = decision.reason;
      if (!decision.hasAccess) {
        this.requireFirstConfiguration = false;
        this.requireCompanyData = false;
        return;
      }

      void this.accountSetupService.check();
      void this.companyDataSetupService.check();
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.currentUser = null;
      this.hasAccess = true;
    }
  }


  private applySubscriptionBadge(): void {
    if (!this.currentUser) {
      return;
    }

    this.packageDisplay.invalidateCache();
    this.packageDisplay.getPackageDisplayName$().pipe(take(1)).subscribe({
      next: (name) => {
        if (!this.currentUser) {
          return;
        }
        const badge = name?.trim() || 'Free';
        this.currentUser = { ...this.currentUser, subscriptionBadge: badge };
      },
      error: () => {
        if (!this.currentUser) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
      },
    });
  }

  toggleNightMode(): void {

  }

  onProfileAction(action: string) {
    if (action === 'logout') {
      this.authenticated = false;
      this.currentUser = null;
      this.keycloak.logout({ redirectUri: window.location.origin });
    } else if (action === 'login') {
      this.keycloak.login();
    } else if (action === 'consultations') {
      window.open('https://ekowitryna.pl/konsultacje/', '_blank');
    }
  }

  onProfileMenuClick(item: string | MenuItem) {
    if (typeof item !== 'string' && item.command) {
      item.command({} as any);
    }
  }

  onMenuClick(item: NavigationItem) {
    if (item.routerLink && !item.disabled) {
      const route = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(route);
    }
    this.closeSidebarMobile();
  }

  onMenuClosed() {
    this.closeSidebarMobile();
  }

  onLanguageChange(lang: string): void {
    this.languageService.setLanguage(lang);
  }


  onSidebarToggle() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeSidebarMobile(): void {
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }
  }

  login() {
    this.keycloak.login();
  }

  private async waitForKeycloakReady(): Promise<void> {
    const timeoutMs = 10_000;
    const pollMs = 50;
    const startedAt = Date.now();

    while (!this.keycloak.didInitialize && Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
  }

}
